type AnalyticsChoice = "accepted" | "declined";
type AnalyticsEvent = "page_view" | "sign_up" | "demo_contact_click" | "pricing_click";

const CONSENT_KEY = "leadcat.analytics-consent.v1";
const configuredId = String(import.meta.env.VITE_LEADCAT_GA4_MEASUREMENT_ID || "").trim();
const measurementId = /^G-[A-Z0-9]+$/.test(configuredId) ? configuredId : "";
let initialized = false;
let sessionChoice: AnalyticsChoice | null = null;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

export function analyticsAvailable(): boolean {
  return Boolean(measurementId) && window.location.hostname === "leadcat.in";
}

export function analyticsChoice(): AnalyticsChoice | null {
  try {
    const choice = window.localStorage.getItem(CONSENT_KEY);
    return sessionChoice ?? (choice === "accepted" || choice === "declined" ? choice : null);
  } catch {
    return sessionChoice;
  }
}

export function setAnalyticsChoice(choice: AnalyticsChoice): void {
  sessionChoice = choice;
  try {
    window.localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    // The choice still applies to the current page when storage is unavailable.
  }
  if (choice === "accepted") {
    if (initialized) {
      (window as AnalyticsWindow).gtag?.("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
    enableAnalytics();
  } else if (initialized) {
    (window as AnalyticsWindow).gtag?.("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

export function enableAnalytics(): void {
  if (!analyticsAvailable() || analyticsChoice() !== "accepted" || initialized) return;
  initialized = true;
  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.gtag = (...args: unknown[]) => { analyticsWindow.dataLayer?.push(args); };
  analyticsWindow.gtag("js", new Date());
  analyticsWindow.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  analyticsWindow.gtag("config", measurementId, { send_page_view: false });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

export function recordAnalyticsEvent(event: AnalyticsEvent, params: Record<string, string> = {}): void {
  if (!analyticsAvailable() || analyticsChoice() !== "accepted") return;
  enableAnalytics();
  (window as AnalyticsWindow).gtag?.("event", event, params);
}

export function recordCurrentPageView(): void {
  recordAnalyticsEvent("page_view", {
    page_location: `${window.location.origin}${window.location.pathname}`,
    page_title: document.title,
  });
}
